/**
 * Slugify a value into a URL-safe slug.
 *
 * Deliberately free of any quantifier-over-variable-length regex (no `+`/`*`
 * applied to the input): the only regexes are fixed single-char literal
 * replacements and a single-character-class diacritic strip — all linear-time.
 * The collapse-runs + trim-hyphens step runs in one O(n) pass instead of
 * `/[^a-z0-9]+/g` + `/^-+|-+$/g`, the latter of which a scanner flags as
 * polynomial-backtracking (ReDoS) on adversarial input.
 *
 * Lives in `packages/shared` because both the backend (Standort/Teacher slugs,
 * `ort:*` and `location:*` tag values) and the website (assembling the request
 * tag bag) must produce byte-identical results. `packages/backend` re-exports it
 * from `locations-import/services/slugify.utils` for its existing callers.
 */
export function slugify(value: string): string {
  const normalized = value
    .toLowerCase()
    .replaceAll('ä', 'ae')
    .replaceAll('ö', 'oe')
    .replaceAll('ü', 'ue')
    .replaceAll('ß', 'ss')
    .normalize('NFKD')
    .replaceAll(/[̀-ͯ]/g, ''); // strip combining diacritical marks

  let slug = '';
  let pendingHyphen = false;
  for (const ch of normalized) {
    const isAlphanumeric = (ch >= 'a' && ch <= 'z') || (ch >= '0' && ch <= '9');
    if (isAlphanumeric) {
      // Emit a separator only *between* alphanumeric runs — never leading/trailing.
      if (pendingHyphen && slug.length > 0) {
        slug += '-';
      }
      pendingHyphen = false;
      slug += ch;
    } else {
      pendingHyphen = true;
    }
  }
  return slug;
}
